import React, { useMemo, useState } from 'react';
import {
  CardIcon,
  CardWithTagIcon,
  DownloadIcon2,
  DownloadIconWithGreyBg,
  PaymentCheckIcon,
  ScanQRIcon,
  SendIconWithGreyBg,
  TTPIcon
} from 'assets/icons';
import { formatAmount, formatDecimal } from 'utils/amountUtils';
import { InvoiceByIdV2 } from '../invoices.model';
import { VoidFn } from 'types/baseTypes';
import { InvoicePaymentMethodTile } from './InvoicePaymentMethodTile';
import Divider from 'components/divider/Divider';
import If from 'components/If/If';
import ChargeCardOnFileModal from '../card-on-file/ChargeCardOnFileModal';
import ChargeCardOnFileSuccess from '../card-on-file/ChargeCardOnFileSuccess';
import { CustomerCard } from 'containers/customers/customers.model';
import { useDeleteCustomerCardMutation } from 'containers/customers/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectIsAdmin,
  selectIsOwner
} from 'containers/app/appSlice';
import CustomerAddCOFModal from 'containers/customers/components/CustomerAddCOFModal';
import ChargeCardOnFileError from '../card-on-file/ChargeCardOnFileError';
import { PaymentMethods } from 'constants/common';
import { NotifierTypes } from 'constants/notifierConstants';
import DeleteCOFModal from 'containers/customers/components/DeleteCOFModal';
import { PaymentMode } from 'containers/settings/settings.model';
import { ChargeCardErrorState, ChargeCardSuccessState } from '../types';
import { initialChargeCardErrorState, initialChargeCardSuccessState } from '../constants';
import { showNotification } from 'utils/Notifier';
import { checkFeatureStatus } from '../card-on-file/utils';
import ManageCardModal from '../card-on-file/ManageCardModal';
import { getCustomerFullName } from 'containers/customers/Utils';

import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsCardOnFile } from '../card-on-file/events';
import { InvoiceAmountHeader } from './InvoiceAmountHeader';
import { PartialAmountHeader } from './PartialAmountHeader';

interface SharePendingInvoiceMainSectionProps {
  invoiceData: InvoiceByIdV2;
  handleManualKeyIn: VoidFn;
  handleRecordPayment: VoidFn;
  handleSendInvoice: VoidFn;
  handleDownloadInvoice: VoidFn;
  handlePayWithQRCodeClick: VoidFn;
  customerCardsData: CustomerCard[];
  closeSuccessModalCallback?: VoidFn;
  isLoadingCards?: boolean;
  paymentModes: PaymentMode[];
  handleSendInvoiceOnPaymentSuccess?: VoidFn;
  customAmount?: number;
  handleEditAmountClick?: VoidFn;
  handleCollectPartialAmountClick?: VoidFn;
  isForCollectPayment?: boolean;
  downloadAsLink?: boolean;
}

export const SharePendingInvoiceMainSection: React.FC<SharePendingInvoiceMainSectionProps> = ({
  invoiceData,
  handleManualKeyIn,
  handleRecordPayment,
  handleSendInvoice,
  handleDownloadInvoice,
  handlePayWithQRCodeClick,
  customerCardsData,
  closeSuccessModalCallback,
  isLoadingCards = false,
  paymentModes,
  handleSendInvoiceOnPaymentSuccess,
  customAmount,
  handleEditAmountClick,
  handleCollectPartialAmountClick,
  downloadAsLink = false,
  isForCollectPayment = false
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const isOwner = useAppSelector(selectIsOwner);
  const isAdmin = useAppSelector(selectIsAdmin);

  const dispatch = useAppDispatch();

  const [showSuccessModal, setShowSuccessModal] = useState<ChargeCardSuccessState>(initialChargeCardSuccessState);
  const [openChargeOnCardModal, setOpenChargeOnCardModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CustomerCard | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openAddCardModal, setOpenAddCardModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<ChargeCardErrorState>(initialChargeCardErrorState);
  const [openManageCardModal, setOpenManageCardModal] = useState(false);
  const amountPayable = customAmount || invoiceData?.pendingAmount;

  const [deleteCustomerCard, { isLoading: isDeleting }] = useDeleteCustomerCardMutation();

  const onChargeCardSuccess = ({ cardData, transactionId }: { cardData: CustomerCard; transactionId: string }) => {
    handleChargeCardOnFileClose();
    setShowSuccessModal({ cardData, transactionId, isOpen: true });
  };

  const handleSendInvoiceOnChargeCardSuccess = () => {
    if (handleSendInvoiceOnPaymentSuccess) {
      handleSendInvoiceOnPaymentSuccess();
    } else {
      handleSendInvoice();
    }
  };

  const handleChargeCardOnFileClose = () => {
    setOpenChargeOnCardModal(false);
  };
  const handleFeatureNotEnabled = (message: string, notificationId: string) => {
    showNotification(dispatch, NotifierTypes.ERROR, message, 4000, notificationId);
  };

  const handleCOFLimitExceeded = (maximumAmount: number) => {
    showNotification(
      dispatch,
      NotifierTypes.ERROR,
      `Charge card on file can't be used for this payment. 
                            It's available only for amounts up to 
                            $${formatDecimal(formatAmount(maximumAmount))}. 
                            Please use a different method or contact support for help.`,
      4000,
      'charge-card-on-file-notification-id'
    );
  };

  const handleChargeCardOnFileClick = () => {
    if (!customerPermissions.viewCustomerCards) {
      showNotification(dispatch, NotifierTypes.ERROR, 'You do not have permission to charge cards on file.');
      return;
    }
    const cardOnFile = paymentModes.find(mode => mode.name === PaymentMethods.CARD_ON_FILE);
    const cofFeatureStatus = checkFeatureStatus(cardOnFile, isOwner, isAdmin, 'cardOnFile');

    if (!cofFeatureStatus.isEnabled) {
      handleFeatureNotEnabled(cofFeatureStatus.message, 'card-on-file-notification-id');
      return;
    }

    if (+amountPayable <= +cardOnFile?.maximumAmount) {
      setOpenChargeOnCardModal(true);
    } else {
      handleCOFLimitExceeded(+cardOnFile?.maximumAmount);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(initialChargeCardSuccessState);
    closeSuccessModalCallback?.();
  };

  const onDeleteCard = (card: CustomerCard) => {
    setSelectedCard(card);
    setShowDeleteModal(true);
    setOpenChargeOnCardModal(false);

    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardClicked, {
      'Customer Id': card.customerId,
      'Card Network': card.network,
      'Card Last 4': card.cardLast4,
      'Invoice Id': invoiceData?.invoiceId,
      Source: 'Invoice COF'
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCustomerCard({
        teamId: currentTeam.id,
        customerId: invoiceData.invoiceCustomer.id,
        cardId: selectedCard.id
      }).unwrap();

      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardSuccess, {
        'Customer Id': selectedCard?.customerId,
        'Card Network': selectedCard?.network,
        'Card Last 4': selectedCard?.cardLast4,
        'Invoice Id': invoiceData?.invoiceId,
        Source: 'Invoice COF'
      });
    } catch (error) {
      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardFailed, {
        'Customer Id': selectedCard?.customerId,
        'Card Network': selectedCard?.network,
        'Card Last 4': selectedCard?.cardLast4,
        'Invoice Id': invoiceData?.invoiceId,
        Error: error?.message || 'Unknown error',
        Source: 'Invoice COF'
      });
    } finally {
      setShowDeleteModal(false);
      setOpenManageCardModal(false);
      setSelectedCard(null);
      setOpenChargeOnCardModal(true);
    }
  };

  const handleCancelDelete = () => {
    if (selectedCard) {
      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardCancelled, {
        'Customer Id': selectedCard.customerId,
        'Card Network': selectedCard.network,
        'Card Last 4': selectedCard.cardLast4,
        'Invoice Id': invoiceData?.invoiceId,
        Source: 'Invoice COF'
      });
    }

    setShowDeleteModal(false);
  };

  const onEditCard = (card: CustomerCard) => {
    setSelectedCard(card);
    setOpenAddCardModal(true);
  };

  const getCardDataForModal = useMemo(() => {
    if (selectedCard) {
      // When editing, map CustomerCard to expected format
      const billingAddress = {
        id: 0,
        street: selectedCard.address.line1,
        apartment: selectedCard.address.line2 || '',
        city: selectedCard.address.city,
        state: selectedCard.address.state,
        zipCode: selectedCard.address.zipCode,
        country: selectedCard.address.country,
        addressNote: '',
        addressName: ''
      };
      return {
        name: selectedCard.cardHolderName,
        email: selectedCard.email,
        customerId: selectedCard.customerId,
        billingAddress: selectedCard.address ? { ...billingAddress } : undefined,
        id: selectedCard.id
      };
    }
    // When adding, use original customerCardObject
    if (invoiceData?.invoiceCustomer) {
      return {
        name: getCustomerFullName(invoiceData?.invoiceCustomer) || '',
        email: invoiceData?.invoiceCustomer?.email,
        customerId: invoiceData.invoiceCustomer.id,
        // TODO: Remove this once we have the correct country code in the address from the backend
        billingAddress: invoiceData?.invoiceCustomer?.billingAddress
          ? {
              ...invoiceData?.invoiceCustomer?.billingAddress,
              country:
                invoiceData?.invoiceCustomer?.billingAddress?.country === 'USA'
                  ? 'US'
                  : invoiceData?.invoiceCustomer?.billingAddress?.country
            }
          : undefined
      };
    }
    return null;
  }, [
    selectedCard,
    invoiceData?.invoiceCustomer?.name,
    invoiceData?.invoiceCustomer?.email,
    invoiceData?.invoiceCustomer?.id,
    invoiceData?.invoiceCustomer?.billingAddress
  ]);

  const handleAddCard = () => {
    if (!customerPermissions.createCustomerCards) {
      showNotification(
        dispatch,
        NotifierTypes.WARNING,
        'You do not have permission to add card on file',
        4000,
        'add-card-notification-id'
      );
      return;
    }
    const addCard = paymentModes.find(mode => mode.name === PaymentMethods.CARD_ON_FILE);
    const addCardFeatureStatus = checkFeatureStatus(addCard, isOwner, isAdmin, 'cardOnFile');

    if (!addCardFeatureStatus.isEnabled) {
      handleFeatureNotEnabled(addCardFeatureStatus.message, 'card-on-file-notification-id');
      return;
    }
    setOpenAddCardModal(true);
    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardClicked, {
      'Customer Id': invoiceData?.invoiceCustomer?.id,
      'Invoice Id': invoiceData?.invoiceId,
      Source: 'Invoice Share'
    });
  };

  const handleAddCardClose = () => {
    setOpenAddCardModal(false);
  };

  const handleAddCardSuccess = () => {
    setOpenAddCardModal(false);
    setOpenChargeOnCardModal(true);
  };

  const handleManageCard = (card: CustomerCard) => {
    setSelectedCard(card);
    setOpenManageCardModal(true);
    setOpenChargeOnCardModal(false);

    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFManageCardModalOpen, {
      'Customer Id': card.customerId,
      'Card Network': card.network,
      'Card Last 4': card.cardLast4,
      'Invoice Id': invoiceData?.invoiceId,
      Source: 'Invoice COF'
    });
  };

  const handleManageCardClose = () => {
    setOpenManageCardModal(false);
    setOpenChargeOnCardModal(true);
  };

  const handleErrorOnChargeCard = ({
    cardData,
    message,
    subtitle,
    txnId,
    canRetry
  }: {
    cardData: CustomerCard;
    message: string;
    subtitle?: string;
    txnId: string;
    canRetry?: boolean;
  }) => {
    setShowErrorModal({
      isOpen: true,
      message,
      subtitle,
      txnId,
      cardLast4: cardData.cardLast4,
      network: cardData.network,
      canRetry
    });
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(initialChargeCardErrorState);
  };

  const handleErrorModalTryAgain = () => {
    setShowErrorModal(initialChargeCardErrorState);
    setOpenChargeOnCardModal(true);
  };

  const chargeCardOnFileOption = {
    id: 'collect-payment-charge-card-on-file-btn',
    label: 'Charge card on file',
    icon: (
      <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-primary">
        <CardWithTagIcon className="shrink-0" />
      </div>
    ),
    onClick: handleChargeCardOnFileClick,
    showDividerAfter: false,
    className: 'bg-secondaryBtn py-2.5 pl-4 text-17px font-semibold text-primaryText'
  };

  const paymentOptions = [
    {
      id: 'collect-payment-manual-key-in-btn',
      label: 'Manually enter card details',
      icon: <CardIcon className="h-[30px] w-[30px] shrink-0" />,
      onClick: handleManualKeyIn,
      className: 'bg-secondaryBtn py-2.5 pl-4 text-17px font-semibold text-primaryText'
    },
    ...[chargeCardOnFileOption],
    {
      id: 'invoice-modal-record-payment-btn',
      label: 'Record a payment',
      icon: <PaymentCheckIcon className="h-[30px] w-[30px] shrink-0" />,
      onClick: handleRecordPayment,
      showDividerAfter: true
    },
    {
      id: 'invoice-modal-send-btn',
      label: 'Send invoice',
      icon: <SendIconWithGreyBg />,
      onClick: handleSendInvoice
    },
    ...(downloadAsLink
      ? []
      : [
          {
            id: 'invoice-modal-download-btn',
            label: 'Download invoice',
            icon: <DownloadIconWithGreyBg className="h-[30px] w-[30px] shrink-0" />,
            onClick: handleDownloadInvoice
          }
        ])
  ];

  return (
    <div className="flex w-full flex-col items-center gap-5">
      {isForCollectPayment ? (
        <PartialAmountHeader
          pendingAmount={invoiceData?.pendingAmount}
          customAmount={customAmount}
          handleEditAmountClick={handleEditAmountClick}
          isPartialPaymentEnabled={invoiceData?.partialPaymentEnabled}
        />
      ) : (
        <InvoiceAmountHeader
          invoiceData={invoiceData}
          handleCollectPartialAmountClick={handleCollectPartialAmountClick}
        />
      )}

      <div className="flex w-full flex-col gap-5">
        <div className="flex gap-4">
          <InvoicePaymentMethodTile
            label="Pay with QR code"
            icon={<ScanQRIcon />}
            isDisabled={false}
            onClick={handlePayWithQRCodeClick}
          />
          <InvoicePaymentMethodTile
            label="Tap to Pay on iPhone"
            icon={<TTPIcon className="h-10 w-10" />}
            isDisabled={true}
            onClick={null}
          />
        </div>

        <div className="flex w-full flex-col gap-3">
          {paymentOptions.map(option => (
            <React.Fragment key={option.id}>
              <button
                key={option.id}
                id={option.id}
                className={`flex h-[50px] cursor-pointer items-center gap-[22px] rounded-md
                border border-borderGray px-4 py-2.5 text-17px
                font-medium text-primaryText hover:bg-gray-50 ${option.className}`}
                onClick={option.onClick}
                tabIndex={0}
                role="button"
                aria-label={option.label}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    option.onClick();
                  }
                }}>
                <div className="shrink-0">{option.icon}</div>
                {option.label}
              </button>
              {option.showDividerAfter && <Divider variant="fullWidth" className="my-2 border-borderGray" />}
            </React.Fragment>
          ))}
          <If condition={downloadAsLink}>
            <div className="mt-2 flex items-center justify-center gap-1">
              <DownloadIcon2 />
              <button
                id="download-invoice-instead-btn"
                onClick={handleDownloadInvoice}
                className="text-sbase font-semibold text-secondary">
                Download invoice instead
              </button>
            </div>
          </If>
        </div>
      </div>
      {openChargeOnCardModal && (
        <ChargeCardOnFileModal
          onClose={handleChargeCardOnFileClose}
          invoiceData={invoiceData}
          cardData={customerCardsData}
          onChargeCardSuccessCallback={onChargeCardSuccess}
          onDeleteCard={onDeleteCard}
          onEditCard={onEditCard}
          handleAddCard={handleAddCard}
          onError={handleErrorOnChargeCard}
          isLoadingCards={isLoadingCards}
          cardOnFileData={paymentModes?.find(mode => mode.name === PaymentMethods.CARD_ON_FILE) ?? null}
          onManageCard={handleManageCard}
          amountPayable={amountPayable}
        />
      )}
      {showSuccessModal.isOpen && (
        <ChargeCardOnFileSuccess
          amount={+amountPayable}
          cardData={showSuccessModal.cardData}
          transactionId={showSuccessModal.transactionId}
          onSendInvoice={handleSendInvoiceOnChargeCardSuccess}
          onClose={handleSuccessModalClose}
        />
      )}

      <CustomerAddCOFModal
        isOpen={openAddCardModal}
        onClose={handleAddCardClose}
        cardData={getCardDataForModal}
        successCallback={handleAddCardSuccess}
      />

      {showDeleteModal && (
        <DeleteCOFModal
          onPrimaryBtnClick={handleConfirmDelete}
          onSecondaryBtnClick={handleCancelDelete}
          onCloseIconClick={handleCancelDelete}
          isActionInProgress={isDeleting}
        />
      )}

      {showErrorModal.isOpen && (
        <ChargeCardOnFileError
          isOpen={showErrorModal.isOpen}
          onClose={handleErrorModalClose}
          onTryAgain={handleErrorModalTryAgain}
          amount={+amountPayable}
          cardLast4={showErrorModal.cardLast4}
          txnId={showErrorModal.txnId}
          network={showErrorModal.network}
          message={showErrorModal.message}
          subtitle={showErrorModal.subtitle}
          canRetry={showErrorModal.canRetry}
        />
      )}

      {openManageCardModal && (
        <ManageCardModal
          open={openManageCardModal}
          cardData={selectedCard}
          onClose={handleManageCardClose}
          onDeleteCard={onDeleteCard}
        />
      )}
    </div>
  );
};
